import { useState, useEffect, useCallback } from 'react';
import { Clock, Download, Search } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { api, Transaction, TransactionFilters } from '../../lib/api';
import { calculateDisplayRate, formatDisplayRate } from '../../lib/format';
import { Dropdown } from '../ui/Dropdown';
import { TransactionTable } from '../transactions/TransactionTable';
import {
  DATE_PRESET_OPTIONS,
  type StatusFilter,
} from '../transactions/constants';
import {
  dateInRangeFromPreset,
  dateOutRangeFromPreset,
  localDateStr,
} from '../transactions/dateHelpers';

interface TransactionHistoryTableProps {
  refreshKey?: number;
}

export function TransactionHistoryTable({ refreshKey }: TransactionHistoryTableProps) {
  const { currentEntity } = useEntity();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [selectedDateIn, setSelectedDateIn] = useState<string[]>([]);
  const [selectedDateOut, setSelectedDateOut] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [currencies, setCurrencies] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    api.admin.currencies.list().then((res: any) => {
      setCurrencies(
        res.data
          .filter((c: any) => c.is_active)
          .map((c: any) => ({ value: c.code, label: c.code }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
      );
    }).catch(() => {});
  }, []);

  const fetchTransactions = useCallback(async () => {
    if (!currentEntity?.id) return;
    try {
      setLoading(true);
      setError(null);

      const params: TransactionFilters = {
        limit: 50,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        currencies: selectedCurrencies.length > 0 ? selectedCurrencies : undefined,
        clientSearch: debouncedSearch || undefined,
        includeArchived: showArchived || undefined,
        ...dateInRangeFromPreset(selectedDateIn[0]),
        ...dateOutRangeFromPreset(selectedDateOut[0]),
      };

      const result = await api.transactions.list(currentEntity.id, params);
      setTransactions(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [
    currentEntity?.id,
    statusFilter, selectedCurrencies, debouncedSearch,
    selectedDateIn, selectedDateOut, showArchived,
  ]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions, refreshKey]);

  const handleExport = () => {
    if (!transactions.length) return;
    const rows = transactions.map(tx => [
      tx.id, tx.createdAt, tx.transactionDate || '', tx.type,
      tx.clientName || '', tx.employeeName || '',
      tx.amountIn || '', tx.currencyIn || '',
      tx.amountOut || '', tx.currencyOut || '',
      formatDisplayRate(calculateDisplayRate(tx)), tx.status, tx.comment || '',
    ]);
    const csv = [
      ['ID','Дата IN','Дата OUT','Тип','Клиент','Кассир','Приход','Вал.','Расход','Вал.','Курс','Статус','Комментарий'].join(','),
      ...rows.map(r => r.map(c => `"${c}"`).join(',')),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `transactions_${localDateStr()}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-surface rounded-xl shadow-sm border border-border flex flex-col min-h-[300px]">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="px-5 py-4 border-b border-border space-y-3">

        <div className="flex justify-between items-center">
          <h3 className="font-bold text-foreground flex items-center gap-2">
            <Clock size={18} className="text-foreground-muted" />
            Транзакции
          </h3>
          <button
            onClick={handleExport}
            disabled={!transactions.length}
            className="p-1.5 text-foreground-muted hover:text-foreground hover:bg-surface-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Экспорт CSV"
          >
            <Download size={16} />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="inline-flex bg-background rounded-lg p-1 border border-border">
            {(['all', 'open', 'closed'] as StatusFilter[]).map(s => (
              <button
                key={s}
                className={`px-3 py-1 rounded-md text-xs font-medium cursor-pointer transition-all ${
                  statusFilter === s ? 'bg-surface text-foreground shadow-sm' : 'text-foreground-muted hover:text-foreground'
                }`}
                onClick={() => setStatusFilter(s)}
              >
                {s === 'all' ? 'Все' : s === 'open' ? 'Открытые' : 'Закрытые'}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Dropdown size="sm" options={currencies}           selected={selectedCurrencies} onChange={setSelectedCurrencies} placeholder="Валюты" multiple />
            <Dropdown size="sm" options={DATE_PRESET_OPTIONS}  selected={selectedDateIn}     onChange={setSelectedDateIn}     placeholder="Дата IN" />
            <Dropdown size="sm" options={DATE_PRESET_OPTIONS}  selected={selectedDateOut}    onChange={setSelectedDateOut}    placeholder="Дата OUT" />
            <label className="flex items-center gap-1.5 text-xs text-foreground cursor-pointer px-2.5 py-1.5 bg-surface border border-border rounded-lg hover:border-foreground-muted transition-colors">
              <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="w-3.5 h-3.5 accent-ctp-blue" />
              Архив
            </label>
          </div>
        </div>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
          <input
            type="text"
            placeholder="Поиск по клиенту, описанию..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full py-1.5 pl-8 pr-3 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-ctp-blue"
          />
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <TransactionTable
        transactions={transactions}
        loading={loading}
        error={error}
        onRetry={fetchTransactions}
      />
    </div>
  );
}
